import { useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Radar, XCircle } from 'lucide-react';
import type { DashboardChatThreadWaitProposalResult } from '@shared/types/dashboard-chat';
import type {
  ResolveThreadChatWaitSelection,
  ThreadChatWaitMode,
} from '@shared/types/thread-work';
import { Button } from '@/components/ui/Button';

const toLocalDateTimeInput = (iso: string): string => {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusCopy = {
  confirmed: {
    label: 'Attente confirmée',
    className: 'text-emerald-800',
  },
  rejected: {
    label: 'Proposition refusée',
    className: 'text-slate-700',
  },
  expired: {
    label: 'Proposition expirée',
    className: 'text-amber-800',
  },
} as const;

export const ThreadWaitProposalCard = ({
  result,
  resolving,
  resolutionInProgress,
  error,
  onConfirm,
  onReject,
}: {
  result: DashboardChatThreadWaitProposalResult;
  resolving: boolean;
  resolutionInProgress: boolean;
  error: string | null;
  onConfirm: (selection: ResolveThreadChatWaitSelection) => void;
  onReject: () => void;
}) => {
  const { proposal } = result;
  const [mode, setMode] = useState<ThreadChatWaitMode>(proposal.selectedMode);
  const [targetAt, setTargetAt] = useState(() => toLocalDateTimeInput(proposal.targetAt));
  const [timezone] = useState(proposal.timezone);
  const [reason, setReason] = useState(proposal.reason);
  const [validationError, setValidationError] = useState<string | null>(null);
  const selectedOption = useMemo(
    () => proposal.options.find((option) => option.mode === mode) ?? proposal.options[0],
    [mode, proposal.options]
  );
  const pending = proposal.status === 'pending';

  const selectMode = (nextMode: ThreadChatWaitMode) => {
    setMode(nextMode);
    const option = proposal.options.find((candidate) => candidate.mode === nextMode);
    if (option?.suggestedTargetAt) {
      setTargetAt(toLocalDateTimeInput(option.suggestedTargetAt));
    }
    setValidationError(null);
  };

  const confirm = () => {
    const parsedTarget = new Date(targetAt);
    if (
      !Number.isFinite(parsedTarget.getTime()) ||
      parsedTarget.getTime() <= Date.now()
    ) {
      setValidationError('Choisissez une date et une heure dans le futur.');
      return;
    }
    if (
      selectedOption?.maximumTargetAt &&
      parsedTarget.getTime() > new Date(selectedOption.maximumTargetAt).getTime()
    ) {
      setValidationError('Cette échéance dépasse la fenêtre de surveillance disponible.');
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setValidationError('Indiquez pourquoi cette attente est utile.');
      return;
    }
    setValidationError(null);
    onConfirm({
      mode,
      targetAt: parsedTarget.toISOString(),
      timezone,
      reason: trimmedReason,
    });
  };

  const resolved = proposal.status === 'pending' ? null : statusCopy[proposal.status];
  const Icon = selectedOption?.coverage === 'exact' ? Radar : Clock3;

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-sm">
      <div className="border-b border-domino-cardBorder px-3 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-domino-aubergine">
            <Icon className="h-3.5 w-3.5" />
            {proposal.operation === 'reschedule'
              ? 'Modifier l’attente'
              : 'Créer une attente'}
          </span>
          {resolved && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-medium ${resolved.className}`}
            >
              {proposal.status === 'confirmed' ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5" />
              )}
              {resolved.label}
            </span>
          )}
        </div>
        <p className="mt-3 text-sm font-semibold text-domino-ink">
          {result.sourceDecision.topicTitle}
        </p>
        <p className="mt-1 text-xs leading-5 text-domino-inkSoft">
          {selectedOption?.description}
        </p>
      </div>

      <div className="space-y-3 px-3 py-3">
        <fieldset disabled={!pending || resolutionInProgress}>
          <legend className="text-[11px] font-semibold uppercase tracking-[0.12em] text-domino-inkSoft">
            Type d’attente
          </legend>
          <div className="mt-2 grid gap-2">
            {proposal.options.map((option) => (
              <label
                key={option.mode}
                className="flex cursor-pointer items-start gap-2 rounded-xl border border-domino-cardBorder px-3 py-2 text-xs text-domino-ink"
              >
                <input
                  type="radio"
                  name={`thread-wait-mode-${proposal.id}`}
                  value={option.mode}
                  checked={mode === option.mode}
                  onChange={() => selectMode(option.mode)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">{option.label}</span>
                  <span className="mt-0.5 block text-domino-inkSoft">
                    {option.coverage === 'exact'
                      ? 'Surveillance exacte'
                      : 'Rappel seulement'}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <label className="mt-3 block text-xs font-semibold text-domino-ink">
            {mode === 'time' ? 'Revenir le' : 'Échéance de secours'}
            <input
              type="datetime-local"
              value={targetAt}
              onChange={(event) => setTargetAt(event.target.value)}
              className="mt-1.5 h-10 w-full rounded-xl border border-domino-cardBorder bg-domino-warm px-3 text-sm font-normal text-domino-ink outline-none focus:border-domino-prune"
            />
          </label>
          <p className="mt-1 text-[11px] text-domino-inkSoft">Fuseau : {timezone}</p>

          <label className="mt-3 block text-xs font-semibold text-domino-ink">
            Pourquoi attendre ?
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              maxLength={500}
              className="mt-1.5 w-full resize-none rounded-xl border border-domino-cardBorder bg-domino-warm px-3 py-2 text-sm font-normal text-domino-ink outline-none focus:border-domino-prune"
            />
          </label>
        </fieldset>

        {(validationError || error) && (
          <p className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
            {validationError ?? error}
          </p>
        )}

        {pending && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={resolving}
              disabled={resolutionInProgress && !resolving}
              onClick={confirm}
            >
              {proposal.operation === 'reschedule'
                ? 'Confirmer la modification'
                : 'Confirmer l’attente'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={resolutionInProgress}
              onClick={onReject}
            >
              Conserver l’état actuel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
